import { motion } from "framer-motion";
import { AlertTriangle, RefreshCw, WifiOff, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { LockedNote } from "@/features/plans/gate";
import { planProblem } from "@/features/plans/messages";
import { isApiError } from "@/lib/api/errors";
import { openCreateOrg } from "@/lib/reauth";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { Button } from "./button";
import { Skeleton } from "./misc";

/** An empty state that explains what will appear here and how to get it. */
export function EmptyState({ icon: Icon, title, hint, action, className, art }: { icon: LucideIcon; title: ReactNode; hint?: ReactNode; action?: ReactNode; className?: string; art?: ReactNode }) {
  return (
    <div className={cn("flex flex-col items-center px-6 py-12 text-center", className)}>
      {art ?? <EmptyArt icon={Icon} />}
      <p className="text-[15px] font-semibold text-ink">{title}</p>
      {hint && <p className="mt-1.5 max-w-sm text-[13px] leading-relaxed text-ink-3">{hint}</p>}
      {action && <div className="mt-5 flex flex-wrap justify-center gap-2">{action}</div>}
    </div>
  );
}

/**
 * The empty-state illustration: two soft cards fanned behind a floating tile with the
 * screen's icon, a brand glow and a few sparkles. Drawn with tokens, so it fits both themes.
 */
export function EmptyArt({ icon: Icon }: { icon: LucideIcon }) {
  // Malfak motif: two "files" (folded saffron corner) fanned behind a floating speech-tile.
  const file = (cls: string, lines: [string, string]) => (
    <div className={cn("absolute h-[68px] w-[96px] overflow-hidden rounded-2xl border border-line bg-surface-2 shadow-xs", cls)}>
      <span className="absolute end-0 top-0 size-4 rounded-es-lg bg-[color-mix(in_srgb,var(--saffron)_55%,var(--surface-2))]" />
      <div className={cn("m-3 mt-4 h-2 rounded-full bg-line-2", lines[0])} />
      <div className={cn("mx-3 h-2 rounded-full bg-line", lines[1])} />
    </div>
  );
  return (
    <div className="relative mb-5 h-[108px] w-[156px]" aria-hidden>
      <div className="absolute inset-0 scale-125 rounded-full opacity-70 blur-2xl [background:var(--brand-grad-soft)]" />
      {file("start-1 top-6 -rotate-[9deg]", ["w-12", "w-8"])}
      {file("end-1 top-7 rotate-[8deg]", ["ms-auto w-10", "ms-auto w-14"])}
      <motion.div
        className="absolute start-1/2 top-1 ltr:-translate-x-1/2 rtl:translate-x-1/2"
        animate={{ y: [0, -5, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      >
        <div className="relative grid size-16 place-items-center rounded-[20px] border border-line bg-surface text-accent shadow-raised">
          <Icon className="size-7" />
          {/* the speech tail of the Malfak mark */}
          <span className="absolute -bottom-[7px] start-3 size-3.5 rotate-45 rounded-[3px] border-e border-b border-line bg-surface" />
        </div>
      </motion.div>
      <svg className="absolute end-3 -top-1 size-3.5 text-saffron" viewBox="0 0 12 12">
        <path d="M6 0l1.4 4.6L12 6 7.4 7.4 6 12 4.6 7.4 0 6l4.6-1.4z" fill="currentColor" />
      </svg>
      <span className="absolute start-6 bottom-1 size-1 rounded-full bg-accent" />
      <span className="absolute end-1 bottom-4 size-1 rounded-full bg-violet" />
    </div>
  );
}

/** Explains a failed load in plain words, offers a retry, and shows the request id for support. */
export function ErrorState({ error, onRetry, className, compact }: { error: unknown; onRetry?: () => void; className?: string; compact?: boolean }) {
  const { t } = useT();
  const api = isApiError(error) ? error : null;
  const offline = api?.isNetwork;
  // plans (ALP-80/81): "AI budget for this month is used up", "limit reached (x of y)", "not included in your plan"
  if (api?.isNoOrganization) {
    return (
      <div role="alert" className={cn("flex flex-col items-center text-center", compact ? "px-4 py-6" : "px-6 py-12", className)} data-testid="no-org-problem">
        <p className="text-[14.5px] font-semibold text-ink">{t("common.noOrg.title")}</p>
        <p className="mt-1 max-w-sm text-[13px] text-ink-3">{t("common.noOrg.hint")}</p>
        <Button size="sm" variant="brand" className="mt-4" onClick={openCreateOrg}>
          {t("common.noOrg.create")}
        </Button>
      </div>
    );
  }
  const plan = planProblem(error);
  if (plan) {
    return (
      <div role="alert" className={cn("flex flex-col items-center text-center", compact ? "px-4 py-6" : "px-6 py-12", className)} data-testid="plan-problem" data-kind={plan.kind}>
        <div className="mb-3 grid size-12 place-items-center rounded-2xl bg-warn-soft text-warn">
          <AlertTriangle className="size-5" />
        </div>
        <p className="text-[14.5px] font-semibold text-ink">{plan.message}</p>
        {plan.detail && <p className="mt-1 max-w-sm text-[13px] text-ink-3">{plan.detail}</p>}
        <LockedNote className="mt-3" {...(plan.feature ? { feature: plan.feature } : {})} {...(plan.limit ? { limit: plan.limit } : {})} />
      </div>
    );
  }
  const message = !api
    ? t("common.states.errorHint")
    : api.isNetwork
      ? t("common.errors.network")
      : api.isForbidden
        ? t("common.errors.forbidden")
        : api.isNotFound
          ? t("common.errors.notFound")
          : api.isRateLimited
            ? t("common.errors.rateLimited")
            : api.status >= 500
              ? t("common.errors.server")
              : (api.problem?.detail ?? api.problem?.title ?? t("common.states.errorHint"));
  return (
    <div role="alert" className={cn("flex flex-col items-center text-center", compact ? "px-4 py-6" : "px-6 py-12", className)}>
      <div className="mb-3 grid size-12 place-items-center rounded-2xl bg-bad-soft text-bad">{offline ? <WifiOff className="size-5" /> : <AlertTriangle className="size-5" />}</div>
      <p className="text-[14.5px] font-semibold text-ink">{t("common.states.error")}</p>
      <p className="mt-1 max-w-sm text-[13px] text-ink-3">{message}</p>
      {api?.requestId && (
        <p className="mt-2 text-[11px] text-ink-3">
          {t("common.states.requestId")}: <code dir="ltr" className="font-mono">{api.requestId.slice(0, 8)}</code>
        </p>
      )}
      {onRetry && (
        <Button size="sm" variant="outline" className="mt-4" icon={<RefreshCw />} onClick={onRetry}>
          {t("common.actions.retry")}
        </Button>
      )}
    </div>
  );
}

/** Skeleton rows for lists. */
export function ListSkeleton({ rows = 6, avatar = true, className }: { rows?: number; avatar?: boolean; className?: string }) {
  return (
    <div className={cn("space-y-1 p-2", className)} role="status" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3 rounded-xl p-2.5">
          {avatar && <Skeleton className="size-9 shrink-0 rounded-full" />}
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3 w-1/3" />
            <Skeleton className="h-3" style={{ width: `${55 + ((i * 17) % 40)}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function CardSkeleton({ className, lines = 3 }: { className?: string; lines?: number }) {
  return (
    <div className={cn("rounded-[20px] border border-line bg-surface p-5", className)} aria-hidden>
      <Skeleton className="mb-4 h-4 w-1/3" />
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} className="mb-2 h-3" style={{ width: `${90 - i * 15}%` }} />
      ))}
    </div>
  );
}
